import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import varistLogo from "../assets/varist-navbar-logo.svg";
import homeIcon from "../assets/home.svg";
import "./AllScans.scss";
import { toggleFavorite, toggleVisibility } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  userAnalysesOptions,
  favoriteAnalysesOptions,
  analysisKeys,
} from "../lib/queries";
import AnalysisList from "./AnalysisList";
import ConfirmationModal from "./ConfirmationModal";

export default function MyUploads() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<"uploads" | "favorites">(
    "uploads",
  );
  const [favoriteLoading, setFavoriteLoading] = useState<Set<string>>(
    new Set(),
  );
  const [visibilityLoading, setVisibilityLoading] = useState<Set<string>>(
    new Set(),
  );
  const [pendingVisibilityChange, setPendingVisibilityChange] = useState<{
    analysisId: string;
    currentIsPublic: boolean;
  } | null>(null);

  const enabled = !authLoading && isAuthenticated;

  const {
    data: scans = [],
    isLoading: uploadsLoading,
    isError,
    error,
  } = useQuery({
    ...userAnalysesOptions(100),
    enabled,
  });

  const { data: favoriteScans = [] } = useQuery({
    ...favoriteAnalysesOptions(),
    enabled,
  });

  const loading = authLoading || uploadsLoading;

  const favoriteMutation = useMutation({
    mutationFn: ({
      analysisId,
      isFavorite,
    }: {
      analysisId: string;
      isFavorite: boolean;
    }) => toggleFavorite(analysisId, isFavorite),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: analysisKeys.all });
    },
  });

  const visibilityMutation = useMutation({
    mutationFn: ({
      analysisId,
      isPublic,
    }: {
      analysisId: string;
      isPublic: boolean;
    }) => toggleVisibility(analysisId, isPublic),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...analysisKeys.all, "user"],
      });
    },
  });

  const handleFavoriteToggle = async (
    e: React.MouseEvent,
    analysisId: string,
  ) => {
    e.stopPropagation();
    if (favoriteLoading.has(analysisId)) return;

    const allItems = [...scans, ...favoriteScans];
    const item = allItems.find((s) => s.analysisId === analysisId);
    const newIsFavorite = !item?.isFavorite;

    setFavoriteLoading((prev) => new Set(prev).add(analysisId));

    try {
      await favoriteMutation.mutateAsync({
        analysisId,
        isFavorite: newIsFavorite,
      });
    } catch (err) {
      console.error("Failed to toggle favorite:", err);
    } finally {
      setFavoriteLoading((prev) => {
        const next = new Set(prev);
        next.delete(analysisId);
        return next;
      });
    }
  };

  const handleVisibilityToggle = async (
    e: React.MouseEvent,
    analysisId: string,
    currentIsPublic: boolean,
  ) => {
    e.stopPropagation();
    if (visibilityLoading.has(analysisId)) return;
    setPendingVisibilityChange({ analysisId, currentIsPublic });
  };

  const confirmVisibilityChange = async () => {
    if (!pendingVisibilityChange) return;

    const { analysisId, currentIsPublic } = pendingVisibilityChange;
    setPendingVisibilityChange(null);
    setVisibilityLoading((prev) => new Set(prev).add(analysisId));
    try {
      await visibilityMutation.mutateAsync({
        analysisId,
        isPublic: !currentIsPublic,
      });
    } catch (err) {
      console.error("Failed to toggle visibility:", err);
    } finally {
      setVisibilityLoading((prev) => {
        const next = new Set(prev);
        next.delete(analysisId);
        return next;
      });
    }
  };

  const cancelVisibilityChange = () => {
    setPendingVisibilityChange(null);
  };

  const handleScanClick = (analysisId: string) => {
    navigate(`/analysis/${analysisId}`);
  };

  const handleBack = () => {
    navigate("/");
  };

  const sortedScans = [...scans].sort((a, b) => {
    if (a.isFavorite && !b.isFavorite) return -1;
    if (!a.isFavorite && b.isFavorite) return 1;
    return 0;
  });

  const isMobile = typeof window !== "undefined" && window.innerWidth <= 650;
  return (
    <div className="all-scans-container">
      {!isMobile && (
        <Link to="/" id="all-scans-home-lnk">
          <img className="all-scans-home-icon" src={homeIcon} alt="Home" />
        </Link>
      )}
      <div className="all-scans-header">
        <Link to="/" className="all-scans-logo-link">
          <img className="all-scans-logo" src={varistLogo} alt="Varist logo" />
        </Link>
        <h1>My Uploads</h1>
        <div className="uploads-tabs">
          <button
            className={`tab-btn ${activeTab === "uploads" ? "active" : ""}`}
            onClick={() => setActiveTab("uploads")}
          >
            My Uploads {!loading && `(${scans.length})`}
          </button>
          <button
            className={`tab-btn ${activeTab === "favorites" ? "active" : ""}`}
            onClick={() => setActiveTab("favorites")}
          >
            Favorites {`(${favoriteScans.length})`}
          </button>
        </div>
      </div>

      {isError && (
        <div className="error-message">
          {error instanceof Error
            ? error.message
            : "Failed to load uploads. Please try again later."}
        </div>
      )}

      {!isAuthenticated && !authLoading ? (
        <div className="no-scans">
          <p>Please sign in to view your uploads.</p>
          <button onClick={handleBack} className="home-button">
            Go to Home
          </button>
        </div>
      ) : loading ? (
        <div className="loading-container">
          <p>Loading uploads...</p>
        </div>
      ) : activeTab === "uploads" ? (
        scans.length === 0 ? (
          <div className="no-scans">
            <p>
              You haven't uploaded any files yet. Upload a file to get started!
            </p>
            <button onClick={handleBack} className="home-button">
              Go to Home
            </button>
          </div>
        ) : (
          <div className="scans-grid-wrapper">
            <AnalysisList
              items={sortedScans}
              onFavoriteToggle={handleFavoriteToggle}
              onItemClick={handleScanClick}
              showVisibilityToggle
              visibilityLoading={visibilityLoading}
              onVisibilityToggle={handleVisibilityToggle}
            />
          </div>
        )
      ) : favoriteScans.length === 0 ? (
        <div className="no-scans">
          <p>
            You haven't favorited any scans yet. Click the heart icon on a scan
            to add it here!
          </p>
        </div>
      ) : (
        <div className="scans-grid-wrapper">
          <AnalysisList
            items={favoriteScans}
            onFavoriteToggle={handleFavoriteToggle}
            onItemClick={handleScanClick}
          />
        </div>
      )}

      <ConfirmationModal
        isVisible={pendingVisibilityChange !== null}
        title={
          pendingVisibilityChange?.currentIsPublic
            ? "Make Private?"
            : "Make Public?"
        }
        message={
          pendingVisibilityChange?.currentIsPublic
            ? "This analysis will no longer be visible to other users."
            : "This analysis will be visible to anyone with the link."
        }
        confirmText={
          pendingVisibilityChange?.currentIsPublic
            ? "Make Private"
            : "Make Public"
        }
        cancelText="Cancel"
        onConfirm={confirmVisibilityChange}
        onCancel={cancelVisibilityChange}
      />
    </div>
  );
}
